import { sixesBucketFromSentence, type SixesBucket } from "./sixesMarketBucket";

export type SixesOverUnderLine = {
    side: "o" | "u";
    line: string;
};

export type SixesCellDisplay = {
    shortLabel: string;
    /** Over/under line parsed from the sentence (not shown on the grid; full sentence carries it). */
    overUnderLine: SixesOverUnderLine | null;
    fullSentence: string;
    bannerEmphasis: string;
    statLabel: string;
    /** Market group this cell belongs to (drives the cell colour). */
    bucket: SixesBucket | null;
};

/**
 * Surname particles kept with the final token on grid labels.
 * Case-insensitive single tokens; multi-word forms (`van der`, `de la`) fall out by walking left.
 * Extend this set when a cricket-relevant naming convention shows up in feeds — do not special-case countries in callers.
 */
const SURNAME_PARTICLES = new Set([
    // Dutch / Afrikaans / Flemish (SA, NL, BEL)
    "van",
    "de",
    "den",
    "der",
    "ter",
    "ten",
    "te",
    // German
    "von",
    // Romance / Iberian / Italian (incl. Lankan Portuguese-influenced "de Silva")
    "da",
    "das",
    "do",
    "dos",
    "di",
    "del",
    "della",
    "dei",
    "degli",
    "du",
    "le",
    "la",
    // Arabic / South Asian particle-style prefixes sometimes left as separate tokens
    "al",
    "el",
    "bin",
    "binti",
    "ibn",
]);

/** Last surname token plus any leading particles (`de Kock`, `van der Dussen`, `de Silva`). */
function surnameToken(name: string): string {
    const parts = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (parts.length === 0) return String(name || "").toUpperCase();
    if (parts.length === 1) return parts[0]!.toUpperCase();

    let start = parts.length - 1;
    while (start > 0 && SURNAME_PARTICLES.has(parts[start - 1]!.toLowerCase())) {
        start -= 1;
    }
    return parts.slice(start).join(" ").toUpperCase();
}

/** Same 3-letter convention as cricket logos / scoreboard labels. */
function abbrevTeam(name: string): string {
    const words = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (words.length === 0) return "";
    if (words.length === 1) {
        return words[0]!.replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase();
    }
    return words
        .map((w) => w.replace(/[^A-Za-z0-9]/g, ""))
        .filter(Boolean)
        .map((w) => w[0]!)
        .join("")
        .slice(0, 3)
        .toUpperCase();
}

function normalizeTeamKey(value: string): string {
    return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

/** True when `subject` is exactly one of the fixture team names (not a partial/player match). */
function isFixtureTeamSubject(subject: string, homeTeam: string, awayTeam: string): boolean {
    const key = normalizeTeamKey(subject);
    if (!key) return false;
    return [homeTeam, awayTeam].some((team) => {
        const fullKey = normalizeTeamKey(team);
        return fullKey && fullKey === key;
    });
}

/** Map a sentence token (full name or feed abbrev) to the fixture's canonical team abbrev. */
function fixtureTeamAbbrev(token: string, homeTeam: string, awayTeam: string): string {
    const raw = String(token || "").trim();
    if (!raw) return "";

    const rawUpper = raw.toUpperCase();
    for (const team of [homeTeam, awayTeam]) {
        const full = String(team || "").trim();
        if (!full) continue;
        const abbr = abbrevTeam(full);
        if (!abbr) continue;
        if (rawUpper === full.toUpperCase() || rawUpper === abbr) return abbr;
        const rawKey = normalizeTeamKey(raw);
        const fullKey = normalizeTeamKey(full);
        if (rawKey && fullKey && (fullKey.includes(rawKey) || rawKey.includes(fullKey))) return abbr;
    }
    return abbrevTeam(raw);
}

/**
 * Hard max for a player surname on a grid cell. Shorter names stay intact (CSS truncate
 * clips visually); anything longer gets an explicit "..." so we never soft-chop mid-word
 * into something like "LAWRENC".
 */
const PLAYER_CELL_NAME_MAX = 12;

function playerNameToken(name: string): string {
    const token = surnameToken(name);
    if (token.length <= PLAYER_CELL_NAME_MAX) return token;
    const keep = Math.max(1, PLAYER_CELL_NAME_MAX - 3);
    return `${token.slice(0, keep)}...`;
}

function playerPropShortLabel(player: string, prop: "RUNS" | "SIXES" | "FOURS" | "EXTRAS"): string {
    const name = playerNameToken(player);
    const single = `${name} ${prop}`;
    // Stack when the combined label is tight so the name line can CSS-ellipsis without eating the prop.
    if (single.length > 13) return `${name}\n${prop}`;
    return single;
}

function formatOverUnderLineValue(line: string): string {
    const n = Number(line);
    if (!Number.isFinite(n)) return String(line || "").trim();
    return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, "");
}

function overUnderLineFromSide(sideToken: string, lineToken: string): SixesOverUnderLine {
    const side: "o" | "u" = String(sideToken || "")
        .trim()
        .toLowerCase()
        .startsWith("u")
        ? "u"
        : "o";
    return { side, line: formatOverUnderLineValue(lineToken) };
}

function expandTeamTokens(text: string, homeTeam: string, awayTeam: string): string {
    let out = text;
    const homeShort = abbrevTeam(homeTeam);
    const awayShort = abbrevTeam(awayTeam);
    if (homeShort) out = out.replace(new RegExp(`\\b${homeShort}\\b`, "gi"), homeTeam);
    if (awayShort) out = out.replace(new RegExp(`\\b${awayShort}\\b`, "gi"), awayTeam);
    return out;
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Trim to at most `maxLen` characters on a word boundary — no trailing ellipsis. */
function trimToWords(text: string, maxLen: number): string {
    const t = String(text || "").trim();
    if (t.length <= maxLen) return t;
    const cut = t.slice(0, maxLen);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim();
}

/**
 * Build a short, meaningful label from a market subject (e.g. "Surrey runs in first 6").
 * Replaces a leading team name with its abbreviation and compacts common phrases.
 */
function compactSubjectLabel(title: string, homeTeam: string, awayTeam: string): string {
    let rest = String(title || "").trim();
    let teamPrefix = "";
    for (const team of [homeTeam, awayTeam]) {
        const t = String(team || "").trim();
        if (!t) continue;
        if (new RegExp(`^${escapeRegExp(t)}\\b`, "i").test(rest)) {
            teamPrefix = abbrevTeam(t);
            rest = rest.slice(t.length).trim();
            break;
        }
        const abbr = abbrevTeam(t);
        if (abbr && new RegExp(`^${escapeRegExp(abbr)}\\b`, "i").test(rest)) {
            teamPrefix = abbr;
            rest = rest.slice(abbr.length).trim();
            break;
        }
        if (new RegExp(`\\b${escapeRegExp(t)}\\s*$`, "i").test(rest)) {
            teamPrefix = abbrevTeam(t);
            rest = rest.replace(new RegExp(`\\b${escapeRegExp(t)}\\s*$`, "i"), "").trim();
            break;
        }
    }

    let compacted = rest
        .replace(/\bin the\b/gi, "")
        .replace(/\bruns in first\s+(\d+)\b/gi, "RUNS FIRST $1")
        .replace(/\bruns in first\b/gi, "RUNS FIRST")
        .replace(/\bin first\b/gi, "1ST")
        .replace(/\bfirst\b/gi, "1ST")
        .replace(/\bovers?\b/gi, "OV")
        .replace(/\bsession\b/gi, "SESS")
        .replace(/\bpartnership\b/gi, "P-SHIP")
        .replace(/\binnings\b/gi, "INN")
        .replace(/\s+/g, " ")
        .trim();

    if (/^RUNS FIRST \d+$/i.test(compacted)) {
        compacted = `${compacted} OV`;
    }

    const label = `${teamPrefix} ${compacted}`.trim().toUpperCase();
    if (!label) return "RESULT?";
    return trimToWords(label, 22);
}

/**
 * Red banner span in the cell detail modal: the prediction/outcome after the subject.
 * "MATCH TO HAVE UNDER 12.5 WICKETS" → "HAVE UNDER 12.5 WICKETS"
 * "MATCH HIGHEST INDIVIDUAL SCORE OVER 55.5" → "HIGHEST INDIVIDUAL SCORE OVER 55.5"
 */
function pickEmphasis(upper: string): string {
    // Take the full predicate after TO/WILL (do not stop at IN/AT/ON — that left
    // "IN THE FIRST SESSION" navy while the rest of the same clause was red).
    const toMatch = upper.match(/\bTO\b (.+)$/);
    if (toMatch?.[1]) return toMatch[1].trim();
    const willMatch = upper.match(/\bWILL\b (.+)$/);
    if (willMatch?.[1]) return willMatch[1].trim();
    // Match-titled lines that never use "to" (highest individual score, etc.).
    const matchLead = upper.match(/^MATCH\b (.+)$/);
    if (matchLead?.[1]) return matchLead[1].trim();
    // "OVER/UNDER X — title": emphasise the market title after the dash.
    const dashTitle = upper.match(/^(?:OVER|UNDER) [\d.]+\s*[—–-]\s*(.+)$/);
    if (dashTitle?.[1]) return dashTitle[1].trim();
    return "";
}

function inferStatLabel(text: string): string {
    const lower = text.toLowerCase();
    if (lower.includes("highest runscorer") || lower.includes("top score")) return "TOP SCORE?";
    if (lower.includes("highest wicket") || lower.includes("top bowl")) return "TOP BOWL?";
    if (lower.includes("extra")) return "EXTRAS?";
    if (lower.includes("wicket")) return "WICKET?";
    if (lower.includes("six")) return "SIXES?";
    if (lower.includes("four")) return "FOURS?";
    if (lower.includes("run")) return "RUNS?";
    if (lower.includes("toss")) return "TOSS?";
    if (lower.includes("win")) return "WIN?";
    return "RESULT?";
}

/** Rewrite legacy innings sentences that duplicated "Innings {stat} innings will have". */
function normalizeLegacyInningsSentence(raw: string): string {
    const m = raw.match(/^(.+?) innings will have (over|under) ([\d.]+) (\w+)$/i);
    if (!m) return raw;
    const teamPart = m[1]!.trim();
    const cleaned = teamPart.replace(/\s+Innings\s+(Fours|Sixes|Wickets|Runs)\s*$/i, "").trim();
    if (!cleaned || cleaned === teamPart) return raw;
    return `${cleaned} will have ${m[2]!.toLowerCase()} ${m[3]!} ${m[4]!.toLowerCase()}`;
}

/** Rewrite legacy session sentences that left the raw "Runs in First N" market title. */
function normalizeLegacySessionSentence(raw: string): string {
    const m = raw.match(/^(Over|Under) ([\d.]+) — (.+?) Runs in First (\d+)$/i);
    if (!m) return raw;
    const team = m[3]!.trim();
    return `${team} will score ${m[1]!.toLowerCase()} ${m[2]!} runs in first ${m[4]!} overs`;
}

/** Rewrite legacy highest-individual sentences to the match-titled form. */
function normalizeLegacyHighestIndividualSentence(raw: string): string {
    const over = raw.match(/^A batter will score over ([\d.]+) runs$/i);
    if (over) return `Match highest individual score over ${over[1]!}`;
    const under = raw.match(/^No batter will score over ([\d.]+) runs$/i);
    if (under) return `Match highest individual score under ${under[1]!}`;
    return raw;
}

/** Rewrite legacy "Team — Team of Highest …" fallback sentences (pre type-94/96 cheer shapes). */
function normalizeLegacyTeamOfHighestSentence(raw: string): string {
    let m = raw.match(/^(.+?)\s*[—–-]\s*Team of Highest Runscorer$/i);
    if (m) return `${m[1]!.trim()} will have the highest runscorer`;
    m = raw.match(/^(.+?)\s*[—–-]\s*Team of Highest Wicket Taker$/i);
    if (m) return `${m[1]!.trim()} will have the highest wicket taker`;
    return raw;
}

/** Normalize persisted sentences that predate sentence-builder fixes. */
function normalizeLegacyRunsOffDeliverySentence(raw: string): string {
    // "… runs off delivery 0.1" / "… runs off first delivery" → "… runs first delivery"
    return String(raw || "").replace(
        /\bruns\s+off\s+(?:first\s+)?delivery(?:\s+[\d.]+)?\b/gi,
        "runs first delivery",
    );
}

/** Rewrite legacy fall-of-wicket cheer shapes to `Team first wicket - over/under X`. */
function normalizeLegacyFallOfWicketSentence(raw: string): string {
    let m = raw.match(/^(.+?) will lose their first wicket (after|before) over ([\d.]+)$/i);
    if (m) {
        const side = m[2]!.toLowerCase() === "after" ? "over" : "under";
        return `${m[1]!.trim()} first wicket - ${side} ${m[3]!}`;
    }
    m = raw.match(/^(.+?) first wicket will fall (after|before) over ([\d.]+)$/i);
    if (m) {
        const side = m[2]!.toLowerCase() === "after" ? "over" : "under";
        return `${m[1]!.trim()} first wicket - ${side} ${m[3]!}`;
    }
    m = raw.match(/^First wicket will fall (after|before) over ([\d.]+)$/i);
    if (m) {
        const side = m[1]!.toLowerCase() === "after" ? "over" : "under";
        return `First wicket - ${side} ${m[2]!}`;
    }
    return raw;
}

/** Normalize persisted sentences that predate sentence-builder fixes. */
function normalizeLegacySentence(raw: string): string {
    return normalizeLegacyTeamOfHighestSentence(
        normalizeLegacyHighestIndividualSentence(
            normalizeLegacySessionSentence(
                normalizeLegacyInningsSentence(
                    normalizeLegacyFallOfWicketSentence(normalizeLegacyRunsOffDeliverySentence(raw)),
                ),
            ),
        ),
    );
}

function runsOffDeliveryShortLabel(
    subject: string,
    _line: string,
    homeTeam: string,
    awayTeam: string,
): string {
    const m = String(subject || "")
        .trim()
        .match(/^(.+?)\s+runs(?:\s+off)?\s+(?:first\s+)?delivery\b/i);
    const teamToken = m ? m[1]!.trim() : String(subject || "").trim().split(/\s+/)[0] || "";
    const abbr = teamToken ? fixtureTeamAbbrev(teamToken, homeTeam, awayTeam) : "";
    return abbr ? `${abbr} FIRST DELIVERY` : "FIRST DELIVERY";
}

function matchTotalShortLabel(stat: string): string {
    const normalized = String(stat || "").trim().toLowerCase();
    if (normalized.includes("run out")) return "MATCH RUN OUTS";
    return `MATCH ${stat.toUpperCase()}`;
}

/** Strip trailing odd/even hints from a feed market title before building the grid label. */
function normalizeOddEvenMarketTitle(title: string): string {
    let t = String(title || "").trim();
    if (!t) return t;
    t = t.replace(/,?\s*odd\s*(?:\/|-)?\s*even\s*$/i, "").trim();
    t = t.replace(/\s+odd\s+or\s+even\s*$/i, "").trim();
    // Drop any trailing comma/whitespace left behind once the odd/even hint is stripped
    // (feed titles like "Match Fours, Odd/Even" otherwise leave a dangling "Match Fours,").
    t = t.replace(/[\s,]+$/, "").trim();
    // Bare "{Team} Innings" odd/even lines are fours markets in Sixes feeds.
    if (/\binnings\s*$/i.test(t) && !/\b(fours|sixes|wickets|runs|extras|run outs)\b/i.test(t)) {
        t = `${t} Fours`;
    }
    return t;
}

function oddEvenMarketShortLabel(title: string, homeTeam: string, awayTeam: string): string {
    const normalized = normalizeOddEvenMarketTitle(title);
    if (!normalized) return "O/E";

    const teamAbbr = (token: string) => fixtureTeamAbbrev(token, homeTeam, awayTeam);

    // Match totals: "Match Fours", "Match Sixes", etc.
    // Stack as three rows so cells show "MATCH / FOURS / O/E" instead of truncating
    // the first line to "MATCH F…".
    let m = normalized.match(/^Match\s+(Fours|Sixes|Runs|Wickets|Extras|Run Outs)\s*$/i);
    if (m) return `MATCH\n${m[1]!.toUpperCase()}\nO/E`;

    // Team innings stat: "{Team} Innings Fours|Sixes|Runs|Wickets|Extras"
    m = normalized.match(/^(.+?)\s+Innings\s+(Fours|Sixes|Runs|Wickets|Extras)\s*$/i);
    if (m) {
        const abbr = teamAbbr(m[1]!.trim());
        return `${abbr} INN ${m[2]!.toUpperCase()}\nO/E`;
    }

    // Session: "{Team} Runs in First N"
    m = normalized.match(/^(.+?)\s+Runs in First\s+(\d+)\s*$/i);
    if (m) {
        const abbr = teamAbbr(m[1]!.trim());
        return `${abbr} RUNS 1ST ${m[2]!}\nO/E`;
    }

    // Over: "{Team} Runs in Over N"
    m = normalized.match(/^(.+?)\s+Runs in Over\s+(\d+)\s*$/i);
    if (m) {
        const abbr = teamAbbr(m[1]!.trim());
        return `${abbr} OVER ${m[2]!}\nO/E`;
    }

    const compact = compactSubjectLabel(normalized, homeTeam, awayTeam);
    return compact.length > 14 ? `${compact}\nO/E` : `${compact} O/E`;
}

/** Derive a compact grid label + popup copy from a full cheer sentence. */
export function sixesCellDisplayFromSentence(
    sentence: string,
    opts: { homeTeam?: string; awayTeam?: string } = {},
): SixesCellDisplay {
    const raw = normalizeLegacySentence(String(sentence || "").trim());
    if (!raw) {
        return {
            shortLabel: "",
            overUnderLine: null,
            fullSentence: "",
            bannerEmphasis: "",
            statLabel: "RESULT?",
            bucket: null,
        };
    }

    const homeTeam = opts.homeTeam || "";
    const awayTeam = opts.awayTeam || "";
    let shortLabel = raw.toUpperCase();
    let overUnderLine: SixesOverUnderLine | null = null;

    const teamAbbr = (token: string) => fixtureTeamAbbrev(token, homeTeam, awayTeam);

    let m = raw.match(/^(.+?) will win the match$/i);
    if (m) shortLabel = "MATCH WINNER";

    else if ((m = raw.match(/^(.+?) will win the toss$/i))) shortLabel = "TOSS WINNER";

    else if ((m = raw.match(/^(.+?) \([+-][\d.]+\) will hit more sixes$/i))) shortLabel = "MOST SIXES HCP";

    else if ((m = raw.match(/^(.+?) will hit more sixes \(handicap\)$/i))) shortLabel = "MOST SIXES HCP";

    else if ((m = raw.match(/^(.+?) will hit most sixes$/i))) shortLabel = "MOST SIXES";

    else if ((m = raw.match(/^(.+?) \([+-][\d.]+\) will score more runs$/i))) {
        shortLabel = `${playerNameToken(m[1]!)} HCP`;
    }

    else if ((m = raw.match(/^(.+?) will score more runs \(handicap\)$/i))) {
        shortLabel = `${playerNameToken(m[1]!)} HCP`;
    }

    else if ((m = raw.match(/^(.+?) \([+-][\d.]+\) will hit more fours$/i))) shortLabel = "MOST FOURS HCP";

    else if ((m = raw.match(/^(.+?) will hit more fours \(handicap\)$/i))) shortLabel = "MOST FOURS HCP";

    else if ((m = raw.match(/^(.+?) will hit most fours$/i))) shortLabel = "MOST FOURS";

    else if ((m = raw.match(/^(.+?) \([+-][\d.]+\) will have higher opening partnership$/i)))
        shortLabel = "OPEN P-SHIP HCP";

    else if ((m = raw.match(/^(.+?) will have higher opening partnership \(handicap\)$/i)))
        shortLabel = "OPEN P-SHIP HCP";

    else if ((m = raw.match(/^(.+?) will (?:have )?highest opening partnership$/i)))
        shortLabel = "HIGHEST OPEN P-SHIP";

    else if ((m = raw.match(/^(.+?) will have the highest runscorer$/i))) shortLabel = "TEAM OF TOP SCORER";

    else if ((m = raw.match(/^(.+?) will have the highest wicket taker$/i))) shortLabel = "TEAM OF TOP BOWLER";

    else if ((m = raw.match(/^(.+?) will win highest first over$/i))) shortLabel = "HIGHEST 1ST OVER";

    else if ((m = raw.match(/^(.+?) will win highest first (\d+) overs$/i)))
        shortLabel = `HIGHEST 1ST ${m[2]!} OV`;

    else if ((m = raw.match(/^(.+?) will win highest first session$/i))) shortLabel = "HIGHEST 1ST 6 OV";

    else if ((m = raw.match(/^(.+?) will hit (over|under) ([\d.]+) (sixes|fours)$/i))) {
        shortLabel = playerPropShortLabel(m[1]!, m[4]!.toUpperCase() as "SIXES" | "FOURS");
        overUnderLine = overUnderLineFromSide(m[2]!, m[3]!);
    }

    else if ((m = raw.match(/^(.+?) will score (over|under) ([\d.]+) runs in first (\d+) balls$/i))) {
        shortLabel = `${teamAbbr(m[1]!)} RUNS 1ST ${m[4]!} BALLS`;
        overUnderLine = overUnderLineFromSide(m[2]!, m[3]!);
    }

    else if ((m = raw.match(/^(.+?) will score (over|under) ([\d.]+) runs in first (\d+) overs$/i))) {
        shortLabel = `${teamAbbr(m[1]!)} RUNS 1ST ${m[4]!} OV`;
        overUnderLine = overUnderLineFromSide(m[2]!, m[3]!);
    }

    else if ((m = raw.match(/^(.+?) will score (over|under) ([\d.]+) runs in the first session$/i))) {
        shortLabel = `${teamAbbr(m[1]!)} RUNS 1ST OV`;
        overUnderLine = overUnderLineFromSide(m[2]!, m[3]!);
    }

    else if ((m = raw.match(/^(.+?) will score (over|under) ([\d.]+) runs$/i))) {
        const subject = m[1]!.trim();
        shortLabel = isFixtureTeamSubject(subject, homeTeam, awayTeam)
            ? `${teamAbbr(subject)} RUNS`
            : playerPropShortLabel(subject, "RUNS");
        overUnderLine = overUnderLineFromSide(m[2]!, m[3]!);
    }

    else if ((m = raw.match(/^Match will have (over|under) ([\d.]+) (.+)$/i))) {
        shortLabel = matchTotalShortLabel(m[3]!);
        overUnderLine = overUnderLineFromSide(m[1]!, m[2]!);
    }

    else if ((m = raw.match(/^(.+?) will have (over|under) ([\d.]+) (wickets|fours|sixes|runs|extras)(?: if batting (?:first|second))?$/i))) {
        shortLabel = `${teamAbbr(m[1]!)} INN ${m[4]!.toUpperCase()}`;
        overUnderLine = overUnderLineFromSide(m[2]!, m[3]!);
    }

    else if ((m = raw.match(/^(.+?) innings will have (over|under) ([\d.]+) (\w+)(?: if batting (?:first|second))?$/i))) {
        shortLabel = `${teamAbbr(m[1]!)} INN ${m[4]!.toUpperCase()}`;
        overUnderLine = overUnderLineFromSide(m[2]!, m[3]!);
    }

    else if ((m = raw.match(/^First (\d+) overs will have (over|under) ([\d.]+) runs$/i))) {
        shortLabel = `1ST ${m[1]!} OV RUNS`;
        overUnderLine = overUnderLineFromSide(m[2]!, m[3]!);
    }

    else if ((m = raw.match(/^First session will have (over|under) ([\d.]+) runs$/i))) {
        shortLabel = `1ST OV RUNS`;
        overUnderLine = overUnderLineFromSide(m[1]!, m[2]!);
    }

    else if ((m = raw.match(/^(Over|Under) ([\d.]+) — match highest runs off over$/i))) {
        shortLabel = "MATCH HIGH OVER";
        overUnderLine = overUnderLineFromSide(m[1]!, m[2]!);
    }

    else if ((m = raw.match(/^Match highest individual score (over|under) ([\d.]+)$/i))) {
        shortLabel = "MATCH INDIVIDUAL SCORE";
        overUnderLine = overUnderLineFromSide(m[1]!, m[2]!);
    }

    else if ((m = raw.match(/^(?:A|No) batter will score over ([\d.]+) runs$/i))) {
        shortLabel = "MATCH INDIVIDUAL SCORE";
        overUnderLine = overUnderLineFromSide(raw.toLowerCase().startsWith("no") ? "under" : "over", m[1]!);
    }

    else if ((m = raw.match(/^(.+?) first wicket - (over|under) ([\d.]+)$/i))) {
        shortLabel = `${teamAbbr(m[1]!)} 1ST WKT`;
        overUnderLine = overUnderLineFromSide(m[2]!, m[3]!);
    }

    else if ((m = raw.match(/^First wicket - (over|under) ([\d.]+)$/i))) {
        shortLabel = "FIRST WICKET";
        overUnderLine = overUnderLineFromSide(m[1]!, m[2]!);
    }

    else if ((m = raw.match(/^(.+?) will lose their first wicket after over ([\d.]+)$/i))) {
        shortLabel = `${teamAbbr(m[1]!)} 1ST WKT`;
        overUnderLine = overUnderLineFromSide("over", m[2]!);
    }

    else if ((m = raw.match(/^(.+?) will lose their first wicket before over ([\d.]+)$/i))) {
        shortLabel = `${teamAbbr(m[1]!)} 1ST WKT`;
        overUnderLine = overUnderLineFromSide("under", m[2]!);
    }

    else if ((m = raw.match(/^First wicket will fall after over ([\d.]+)$/i))) {
        shortLabel = "FIRST WICKET";
        overUnderLine = overUnderLineFromSide("over", m[1]!);
    }

    else if ((m = raw.match(/^First wicket will fall before over ([\d.]+)$/i))) {
        shortLabel = "FIRST WICKET";
        overUnderLine = overUnderLineFromSide("under", m[1]!);
    }

    else if ((m = raw.match(/^(.+?) will score more runs than (.+)$/i)))
        shortLabel = `${playerNameToken(m[1]!)} V ${playerNameToken(m[2]!)}`;

    else if ((m = raw.match(/^(Odd|Even)\s*[—–-]\s*(.+)$/i)))
        shortLabel = oddEvenMarketShortLabel(m[2]!.trim(), homeTeam, awayTeam);

    else if ((m = raw.match(/^(Over|Under) ([\d.]+) — (.+)$/i))) {
        overUnderLine = overUnderLineFromSide(m[1]!, m[2]!);
        const title = m[3]!.toLowerCase();
        if (title.includes("wicket")) {
            shortLabel = "FIRST WICKET";
        } else if (title.includes("delivery"))
            shortLabel = runsOffDeliveryShortLabel(m[3]!, m[2]!, homeTeam, awayTeam);
        else if (/session runs$/i.test(m[3]!.trim())) {
            const teamPart = m[3]!.replace(/\s+session runs\s*$/i, "").trim();
            shortLabel = teamPart ? `${fixtureTeamAbbrev(teamPart, homeTeam, awayTeam)} RUNS` : "RUNS";
        } else shortLabel = compactSubjectLabel(m[3]!, homeTeam, awayTeam);
    } else if (raw.length > 22) {
        shortLabel = trimToWords(raw, 22).toUpperCase();
    }

    const expanded = expandTeamTokens(raw, homeTeam, awayTeam);
    const fullSentence = expanded
        .replace(/\bwill\b/gi, "TO")
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();
    const bannerEmphasis = pickEmphasis(fullSentence);
    const statLabel = inferStatLabel(raw);
    const bucket = sixesBucketFromSentence(raw, { homeTeam, awayTeam });

    return { shortLabel, overUnderLine, fullSentence, bannerEmphasis, statLabel, bucket };
}

function titleCaseToken(token: string): string {
    const t = String(token || "").trim();
    if (!t) return "";
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

/** Map PBKS/MI abbreviations in demo labels to fixture team names. */
function demoTeamFromAbbrev(abbrev: string, homeTeam: string, awayTeam: string): string {
    const key = String(abbrev || "").trim().toUpperCase();
    if (key === "PBKS" || key === abbrevTeam(homeTeam)) return homeTeam || "Punjab Kings";
    if (key === "MI" || key === abbrevTeam(awayTeam)) return awayTeam || "Mumbai Indians";
    return homeTeam || "Punjab Kings";
}

/**
 * Turn a demo grid label into a real cheer sentence (same shapes as `buildSixesCheerSentence`).
 * Labels stay short on the grid; this copy powers the detail modal only.
 */
function demoLabelToCheerSentence(label: string, homeTeam: string, awayTeam: string): string {
    const shortLabel = String(label || "").trim().toUpperCase();
    const home = homeTeam || "Punjab Kings";
    const away = awayTeam || "Mumbai Indians";

    const exact: Record<string, string> = {
        "PBKS V MI": `${home} will win the match`,
        "MOST SIXES": `${home} will hit most sixes`,
        "MOST SIXES HCP": `${home} (-1.5) will hit more sixes`,
        "MOST FOURS": `${home} will hit most fours`,
        "MOST FOURS HCP": `${home} (-1.5) will hit more fours`,
        "OPEN P-SHIP HCP": `${home} (-5.5) will have higher opening partnership`,
        "HIGHEST OPEN P-SHIP": `${home} will have highest opening partnership`,
        "TOSS WINNER": `${home} will win the toss`,
        "MATCH WINNER": `${home} will win the match`,
        "HIGH 1ST OVER": `${home} will win highest first over`,
        "FIRST BALL RUNS": "Over 0.5 — First ball runs",
        "FIRST OVER RUNS": `Over 8.5 — ${home} runs in first over`,
        "FIRST WICKET": "First wicket - over 6",
        "FIRST 6 OVERS": `Over 46.5 — ${home} runs in first 6 overs`,
        "PBKS OVER 1": `Over 5.5 — ${home} runs in over 1`,
        "PBKS OVER 6": `Over 8.5 — ${home} runs in over 6`,
        "PBKS OVER 15": `Over 7.5 — ${home} runs in over 15`,
        "PBKS FIRST DELIVERY": `Over 0.5 — ${home} runs first delivery`,
        "PBKS DEL 0.5 RUNS": `Over 0.5 — ${home} runs first delivery`,
        "MI OVER 1": `Over 5.5 — ${away} runs in over 1`,
        "MI OVER 6": `Over 8.5 — ${away} runs in over 6`,
        "MI OVER 15": `Over 7.5 — ${away} runs in over 15`,
        "MI FIRST DELIVERY": `Over 0.5 — ${away} runs first delivery`,
        "MI DEL 0.5 RUNS": `Over 0.5 — ${away} runs first delivery`,
        "PBKS INN RUNS": `${home} will have over 165.5 runs`,
        "MI INN RUNS": `${away} will have over 172.5 runs`,
        "PBKS INN SIXES": `${home} will have over 8.5 sixes`,
        "MI INN SIXES": `${away} will have over 9.5 sixes`,
        "IYER RUNS": "Shreyas Iyer will score over 32.5 runs",
        "SHASHANK SIXES": "Shashank Singh will hit over 0.5 sixes",
        "STOINIS RUNS": "Marcus Stoinis will score over 18.5 runs",
        "INGLIS FOURS": "Phil Inglis will hit over 2.5 fours",
        "ROHIT RUNS": "Rohit Sharma will score over 28.5 runs",
        "SURYA SIXES": "Suryakumar Yadav will hit over 0.5 sixes",
        "TILAK RUNS": "Tilak Varma will score over 24.5 runs",
        "HARDIK FOURS": "Hardik Pandya will hit over 2.5 fours",
        "MATCH RUNS": "Match will have over 340.5 runs",
        "MATCH SIXES": "Match will have over 22.5 sixes",
        "MATCH FOURS": "Match will have over 36.5 fours",
        "MATCH WICKETS": "Match will have over 13.5 wickets",
        "IYER V ROHIT": "Shreyas Iyer will score more runs than Rohit Sharma",
        "SURYA V SHASHANK": "Suryakumar Yadav will score more runs than Shashank Singh",
        "TILAK V STOINIS": "Tilak Varma will score more runs than Marcus Stoinis",
        "HARDIK V INGLIS": "Hardik Pandya will score more runs than Phil Inglis",
        "ISHAN V PRABH": "Ishan Kishan will score more runs than Prabhsimran Singh",
        "MAXWELL V NAMAN": "Glenn Maxwell will score more runs than Naman Dhir",
    };
    if (exact[shortLabel]) return exact[shortLabel];

    const playerProp = shortLabel.match(/^([A-Z]+)\s+(RUNS|SIXES|FOURS)$/);
    if (playerProp) {
        const player = titleCaseToken(playerProp[1]!);
        const prop = playerProp[2]!;
        if (prop === "RUNS") return `${player} will score over 24.5 runs`;
        if (prop === "SIXES") return `${player} will hit over 0.5 sixes`;
        return `${player} will hit over 2.5 fours`;
    }

    const playerH2h = shortLabel.match(/^([A-Z]+)\s+V\s+([A-Z]+)$/);
    if (playerH2h) {
        return `${titleCaseToken(playerH2h[1]!)} will score more runs than ${titleCaseToken(playerH2h[2]!)}`;
    }

    const teamOver = shortLabel.match(/^(PBKS|MI)\s+OVER\s+(\d+)$/);
    if (teamOver) {
        const team = demoTeamFromAbbrev(teamOver[1]!, home, away);
        const overNo = teamOver[2]!;
        const line = overNo === "1" ? "5.5" : overNo === "6" ? "8.5" : "7.5";
        return `Over ${line} — ${team} runs in over ${overNo}`;
    }

    const teamDel = shortLabel.match(/^(PBKS|MI)\s+(?:FIRST DELIVERY|DEL(?:\s+[\d.]+)?(?:\s+RUNS)?)$/);
    if (teamDel) {
        const team = demoTeamFromAbbrev(teamDel[1]!, home, away);
        return `Over 0.5 — ${team} runs first delivery`;
    }

    const inn = shortLabel.match(/^(PBKS|MI)\s+INN\s+(RUNS|SIXES|FOURS|WICKETS)$/);
    if (inn) {
        const team = demoTeamFromAbbrev(inn[1]!, home, away);
        const stat = inn[2]!.toLowerCase();
        const line =
            stat === "runs" ? "165.5" : stat === "sixes" ? "8.5" : stat === "fours" ? "32.5" : "13.5";
        return `${team} will have over ${line} ${stat}`;
    }

    return `${home} — ${shortLabel}`;
}

/** Demo grid labels already short — expand for the detail popup. */
export function sixesCellDisplayFromDemoLabel(label: string, homeTeam: string, awayTeam: string): SixesCellDisplay {
    const shortLabel = String(label || "").trim().toUpperCase();
    const fromSentence = sixesCellDisplayFromSentence(demoLabelToCheerSentence(shortLabel, homeTeam, awayTeam), {
        homeTeam,
        awayTeam,
    });
    return { ...fromSentence, shortLabel };
}
